import { DatePipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { FinanceApiService } from '../finance/finance-api.service';
import { formatCustomerReferenceFromPlatform } from './catalog-customer.util';
import { CatalogService, ProductSubscriptionContract, ProductSubscriptionPlan } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-subscription-contracts',
  imports: [
    AinPageComponent, AinStateComponent, TableModule, ButtonModule, DatePipe, DialogModule, FormsModule,
    SelectModule, CheckboxModule, CustomerSelectorComponent
  ],
  template: `
    <ain-page [title]="t('catalog.subscription.contracts.title')" [description]="t('catalog.subscription.contracts.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('catalog.subscription.contracts.create')" (click)="openCreateContract()"></button>
            <button pButton type="button" [label]="t('catalog.subscription.billing.runDue')" (click)="runDueBilling()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.subscription.billing.processLifecycle')" (click)="processLifecycle()"></button>
            <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>
          @if (billingMessage()) { <p class="message">{{ billingMessage() }}</p> }

          <h3>{{ t('catalog.subscription.contracts.listTitle') }}</h3>
          <p-table [value]="contracts()" [paginator]="true" [rows]="10">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.subscription.contractDetail.customer') }}</th>
                <th>{{ t('catalog.tracking.status') }}</th>
                <th>{{ t('catalog.subscription.contractDetail.periodEnd') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.customerReference || row.id }}</button></td>
                <td>{{ row.status }}</td>
                <td>{{ row.currentPeriodEndUtc | date:'medium' }}</td>
                <td>
                  <button pButton type="button" size="small" severity="secondary"
                    [label]="t('catalog.subscription.contracts.openDetail')"
                    (click)="openDetail(row.id)"></button>
                </td>
              </tr>
            </ng-template>
          </p-table>

          <h3>{{ t('catalog.subscription.plans.title') }}</h3>
          <p-table [value]="plans()" [paginator]="true" [rows]="10">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.subscription.plans.code') }}</th>
                <th>{{ t('catalog.subscription.plans.name') }}</th>
                <th>{{ t('catalog.subscription.plans.amount') }}</th>
                <th>{{ t('catalog.tracking.status') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.planCode }}</td>
                <td>{{ row.name }}</td>
                <td>{{ row.recurringAmount }} {{ row.currencyCode }}</td>
                <td>{{ row.isActive ? t('catalog.hospitality.active') : t('catalog.hospitality.inactive') }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }

      <p-dialog [header]="t('catalog.subscription.contracts.create')" [(visible)]="createVisible" [modal]="true" [style]="{ width: '460px' }">
        <div class="form">
          <label>{{ t('catalog.subscription.plans.title') }}</label>
          <p-select
            [options]="activePlanOptions()"
            optionLabel="label"
            optionValue="value"
            [(ngModel)]="createPlanId"
            [style]="{ width: '100%' }" />
          <label>{{ t('catalog.subscription.contractDetail.customer') }}</label>
          <ain-customer-selector [(ngModel)]="createCustomerId" />
          <div class="check-row">
            <p-checkbox [(ngModel)]="createBillImmediately" [binary]="true" inputId="billNow" />
            <label for="billNow">{{ t('catalog.subscription.contracts.billImmediately') }}</label>
          </div>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="createVisible = false"></button>
          <button pButton type="button" [label]="t('catalog.action.create')" [disabled]="acting()" (click)="submitCreateContract()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 16px; }
    .toolbar { display: flex; gap: 8px; }
    .message { margin: 0; color: #166534; font-weight: 600; }
    h3 { margin: 0; font-size: 1rem; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .form { display: grid; gap: 8px; }
    .check-row { display: flex; align-items: center; gap: 8px; }
  `]
})
export class SubscriptionContractsComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly financeApi = inject(FinanceApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly acting = signal(false);
  readonly plans = signal<ProductSubscriptionPlan[]>([]);
  readonly contracts = signal<ProductSubscriptionContract[]>([]);
  readonly billingMessage = signal('');

  createVisible = false;
  createPlanId = '';
  createCustomerId: string | null = null;
  createBillImmediately = false;

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading.set(true);
    this.billingMessage.set('');
    this.service.listProductSubscriptionPlans().subscribe({
      next: plans => {
        this.plans.set(plans);
        this.service.listProductSubscriptionContracts().subscribe({
          next: contracts => { this.contracts.set(contracts); this.loading.set(false); },
          error: () => { this.contracts.set([]); this.loading.set(false); }
        });
      },
      error: () => { this.plans.set([]); this.loading.set(false); }
    });
  }

  runDueBilling(): void {
    this.service.runProductSubscriptionDueBilling(true).subscribe({
      next: r => {
        this.billingMessage.set(
          this.t('catalog.subscription.billing.runDueResult').replace('{count}', String(r.issuedCount)));
        this.load();
      }
    });
  }

  processLifecycle(): void {
    this.service.processProductSubscriptionLifecycle(true).subscribe({
      next: r => {
        this.billingMessage.set(
          this.t('catalog.subscription.billing.lifecycleResult')
            .replace('{grace}', String(r.enteredGraceCount))
            .replace('{expired}', String(r.expiredCount))
            .replace('{issued}', String(r.issuedCount)));
        this.load();
      }
    });
  }

  openDetail(id: string): void { void this.router.navigate(['/subscription/contracts', id]); }

  activePlanOptions(): { label: string; value: string }[] {
    return this.plans()
      .filter(plan => plan.isActive)
      .map(plan => ({ label: `${plan.planCode} — ${plan.name}`, value: plan.id }));
  }

  openCreateContract(): void {
    const activePlans = this.plans().filter(plan => plan.isActive);
    this.createPlanId = activePlans[0]?.id ?? '';
    this.createCustomerId = null;
    this.createBillImmediately = false;
    this.createVisible = true;
  }

  submitCreateContract(): void {
    if (!this.createPlanId || !this.createCustomerId) {
      this.billingMessage.set(this.t('catalog.customer.required'));
      return;
    }

    this.acting.set(true);
    const customerId = this.createCustomerId;
    this.financeApi.getPlatformCustomer(customerId).subscribe({
      next: customer => {
        this.service.createProductSubscriptionContract({
          planId: this.createPlanId,
          customerId,
          customerReference: formatCustomerReferenceFromPlatform(customer),
          billImmediately: this.createBillImmediately
        }).subscribe({
          next: contract => {
            this.createVisible = false;
            this.billingMessage.set(this.t('catalog.subscription.contracts.created'));
            this.load();
            this.acting.set(false);
            void this.router.navigate(['/subscription/contracts', contract.id]);
          },
          error: () => this.acting.set(false)
        });
      },
      error: () => this.acting.set(false)
    });
  }

  t(key: string): string { return this.localization.translate(key); }
}
